import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, TablePagination, Snackbar, Alert, Typography
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { Select, MenuItem, InputLabel, FormControl } from "@mui/material";
import { axiosInstances } from "./apiConfig";

export default function AdminUI() {
  // const API_BASE = "/api/admins";

  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});


  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);

  const loadAdmins = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances.adminApi.get(`/?page=${pageNum}&size=${size}`);

      const result = res.data;          // whole EndResult
      const payload = result.data || {}; // the { data: [...], total: n } map
      setAdmins(payload.data || []);     // actual array
      setTotal(payload.total || 0);      // total count
    } catch (err) {
      console.error("Error fetching admins", err);
    }
  };



  const validateForm = () => {
    const newErrors = {};
    if (!form.username) newErrors.username = "Username is required";
    if (!form.role) newErrors.role = "Role is required";
    // add other validations as needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  useEffect(() => { loadAdmins(); }, [page, rowsPerPage]);

  // Open Dialog for Add/Edit
  const handleOpen = (admin = {}) => {
    setForm(admin);
    setEditing(!!admin.id);
    setOpen(true);
  };

  const handleClose = () => {
    setForm({});
    setFile(null);
    setOpen(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };



  const handleSave = async () => {

    try {
      const token = localStorage.getItem("adminToken");

      if (file) {
        // Check file type
        const allowedTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // Excel .xlsx
          "application/vnd.ms-excel", // Excel .xls
          "text/csv" // CSV
        ];

        if (!allowedTypes.includes(file.type)) {
          setSnackbar({ open: true, message: "Only Excel or CSV files are allowed!", severity: "error" });
          return;
        }

        const excelFormData = new FormData();
        excelFormData.append("file", file);

        await axiosInstances.adminApi.post(`/uploadAdmins`, excelFormData, {
          // headers: { Authorization: `Bearer ${token}` },
        });
        setSnackbar({ open: true, message: "File uploaded successfully!", severity: "success" });
      } else {
        
        if (!validateForm()) {
          setSnackbar({ open: true, message: "Please fill all required fields", severity: "warning" });
          return;
        }

        const userPayload = { ...form };
        console.log("Sending payload:", userPayload);

        const url = editing ? `/update` : `/save`;

        await axiosInstances.adminApi.post(url, userPayload, {
          headers: {
            "Content-Type": "application/json",
            // Authorization: `Bearer ${token}`,
          },
        });

        setSnackbar({
          open: true,
          message: editing ? "Admin updated successfully!" : "Admin created successfully!",
          severity: "success"
        });
      }

      loadAdmins();
      handleClose();

    } catch (err) {
      if (err.response && err.response.status === 409) {
        setSnackbar({ open: true, message: "Username or Email already exists", severity: "error" });
      } else if (err.response && err.response.status === 401) {
        setSnackbar({ open: true, message: "Unauthorized - please log in as admin", severity: "error" });
      } else {
        setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
      }
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDeleteClick = (id) => {
    // Mark this row as pending delete
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));

    // Start timer to delete from backend after 30s
    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.adminApi.post(`/delete`, { id });
        loadAdmins();
      } catch (err) {
        console.error("Delete failed", err);
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndoClick = (id) => {
    // Cancel backend delete timer
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }

    // Remove from pendingDeletes so delete button reappears
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };
  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    // <Box p={3}>
    <Box p={3} gap={2} sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom>Admin Management</Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={() => handleOpen()}
      >
        Add Admin
      </Button>
      <Button
        variant="outlined"
        href="/Admin.xlsx"
        download
        sx={{ ml: 2 }}
      >
        Download Template
      </Button>

      {/* Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map((adm, index) => (
              <TableRow key={adm.id}>

                {/* serial number based on pagination */}
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                {/* backend ID */}
                <TableCell>{adm.id}</TableCell>
                <TableCell>{adm.fullName}</TableCell>
                <TableCell>{adm.email}</TableCell>
                <TableCell>{adm.username}</TableCell>
                <TableCell>{adm.phoneNumber}</TableCell>
                <TableCell>{adm.role}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(adm)}>
                    <Edit />
                  </IconButton>
                  {pendingDeletes[adm.id] ? (
                    <IconButton color="warning" onClick={() => handleUndoClick(adm.id)}>
                      <Undo />
                    </IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDeleteClick(adm.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {admins.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>

        </Table>

        <TablePagination
          component="div"
          count={total} // total number of rows from backend
          page={page}   // current page index (0-based)
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) => {
            const totalPages = Math.ceil(total / rowsPerPage);
            return `Page ${page + 1} of ${totalPages}`;
          }}
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? "Edit Admin" : "Add Admin"}</DialogTitle>
        <DialogContent>
          {/* Show ID only when editing */}
          {editing && (
            <TextField
              fullWidth
              margin="dense"
              label="Admin ID"
              name="id"
              value={form.id || ""}
              disabled
            />
          )}
          <TextField
            fullWidth margin="dense" label="Full Name" name="fullName"
            value={form.fullName || ""} onChange={handleChange}
          />
          <TextField
            fullWidth margin="dense" label="Email" name="email"
            value={form.email || ""} onChange={handleChange}
          />
          <TextField
            required fullWidth margin="dense" label="Username" name="username"
            value={form.username || ""} onChange={handleChange} error={!!errors.username}
            helperText={errors.username}
          />
          <TextField
            fullWidth margin="dense" label="Phone" name="phoneNumber"
            value={form.phoneNumber || ""} onChange={handleChange}
          />
          {/* <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              name="role"
              value={form.role || ""}
              onChange={handleChange}
            >
              <MenuItem value="VIEWER">Viewer</MenuItem>
              <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
              <MenuItem value="MODERATOR">Moderator</MenuItem>
            </Select>
          </FormControl> */}
          <FormControl fullWidth margin="dense" error={!!errors.role}>
            <InputLabel required>Role</InputLabel>
            <Select
              name="role"
              value={form.role || ""}
              onChange={handleChange}
              label="Role"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="VIEWER">Viewer</MenuItem>
              <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
              <MenuItem value="MODERATOR">Moderator</MenuItem>
            </Select>
            {errors.role && (
              <Typography variant="caption" color="error">
                {errors.role}
              </Typography>
            )}
          </FormControl>

          <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}><span style={{ color: 'red' }}>*</span>fields are mandatory. <br />

          </Typography>
          {!editing && (
            <>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}
